import {
    readTranslationKey,
    stripTranslationKeyMarkers
} from '../../../shared/translationKeyMarkers';

export type RenderedTranslationCandidate = {
    key: string | null;
    text: string;
};

export type RenderedTranslation = RenderedTranslationCandidate & {
    element: HTMLElement;
    setText: (text: string) => void;
    removeMarkers: () => void;
};

const renderedAttributes = ['placeholder', 'title', 'aria-label'];

const attributeSelector = renderedAttributes.map((attribute) => `[${attribute}]`).join(', ');

function isInsideEditor(element: Element | null): boolean {
    return !!element && element.closest('[data-translation-editor]') !== null;
}

function candidateOf(raw: string | null): RenderedTranslationCandidate {
    return { key: readTranslationKey(raw), text: stripTranslationKeyMarkers(raw).trim() };
}

export function findRenderedTranslations(
    matches: (candidate: RenderedTranslationCandidate) => boolean
): RenderedTranslation[] {
    const found: RenderedTranslation[] = [];

    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
        const textNode = node as Text;
        const parent = textNode.parentElement;
        if (parent && !isInsideEditor(parent)) {
            const candidate = candidateOf(textNode.textContent);
            if (matches(candidate)) {
                found.push({
                    ...candidate,
                    element: parent,
                    setText: (text) => {
                        textNode.textContent = text;
                    },
                    removeMarkers: () => {
                        textNode.textContent = stripTranslationKeyMarkers(textNode.textContent);
                    }
                });
            }
        }
        node = walker.nextNode();
    }

    document.body.querySelectorAll<HTMLElement>(attributeSelector).forEach((element) => {
        if (isInsideEditor(element)) {
            return;
        }
        for (const attribute of renderedAttributes) {
            const raw = element.getAttribute(attribute);
            if (raw === null) {
                continue;
            }
            const candidate = candidateOf(raw);
            if (matches(candidate)) {
                found.push({
                    ...candidate,
                    element,
                    setText: (text) => element.setAttribute(attribute, text),
                    removeMarkers: () =>
                        element.setAttribute(
                            attribute,
                            stripTranslationKeyMarkers(element.getAttribute(attribute))
                        )
                });
            }
        }
    });

    return found;
}

export function animateElementOnce(element: HTMLElement, className: string) {
    element.classList.remove(className);
    element.classList.add(className);
    element.addEventListener('animationend', () => element.classList.remove(className), {
        once: true
    });
}

const keyBearingAttributes = ['placeholder', 'title', 'aria-label', 'alt'];

export type RenderedTranslationTarget = {
    key: string;
    element: Element;
};

export function translationTargetAt(target: EventTarget | null): RenderedTranslationTarget | null {
    if (!(target instanceof Element)) {
        return null;
    }

    const candidates: Array<{ text: string | null; element: Element }> = [
        { text: target.textContent, element: target }
    ];
    for (const attribute of keyBearingAttributes) {
        candidates.push({ text: target.getAttribute(attribute), element: target });
        const ancestor = target.closest(`[${attribute}]`);
        if (ancestor && ancestor !== target) {
            candidates.push({ text: ancestor.getAttribute(attribute), element: ancestor });
        }
    }

    for (const candidate of candidates) {
        const key = readTranslationKey(candidate.text);
        if (key !== null) {
            return { key, element: candidate.element };
        }
    }

    return null;
}

export function translationKeyAt(target: EventTarget | null): string | null {
    const found = translationTargetAt(target);
    return found === null ? null : found.key;
}

export const hoveredTranslationClassName = 'translation-editor-hovered';

let hoveredElement: Element | null = null;

export function highlightHoveredTranslation(element: Element) {
    if (hoveredElement === element) {
        return;
    }
    clearHoveredTranslation();
    element.classList.add(hoveredTranslationClassName);
    hoveredElement = element;
}

export function clearHoveredTranslation() {
    if (hoveredElement !== null) {
        hoveredElement.classList.remove(hoveredTranslationClassName);
        hoveredElement = null;
    }
}

const editorContainerId = 'translation-editor-container';

export function translationEditorContainer(): HTMLElement {
    let container = document.getElementById(editorContainerId);
    if (container === null) {
        container = document.createElement('div');
        container.id = editorContainerId;
        document.body.appendChild(container);
    }
    return container;
}

export function keepFocusInsideEditor(): () => void {
    function stopFocusTraps(event: Event) {
        if (event.target instanceof Element && isInsideEditor(event.target)) {
            event.stopImmediatePropagation();
        }
    }

    document.addEventListener('focus', stopFocusTraps, true);
    document.addEventListener('focusin', stopFocusTraps);
    return () => {
        document.removeEventListener('focus', stopFocusTraps, true);
        document.removeEventListener('focusin', stopFocusTraps);
    };
}
